import { Capacitor } from '@capacitor/core'

/** Prefix for Tap to Pay diagnostics (no secrets: never log TPN, merchant keys, or full config). */
export const DEJAVOO_TOP_LOG_PREFIX = '[DejavooToP]'

/** Logs one line; avoid passing objects that may contain `cfg` or credentials. */
function topLog (phase, detail) {
  try {
    if (detail !== undefined && detail !== null && typeof detail === 'object') {
      console.log(`${DEJAVOO_TOP_LOG_PREFIX} ${phase} ${JSON.stringify(detail)}`)
    } else {
      console.log(`${DEJAVOO_TOP_LOG_PREFIX} ${phase}`, detail)
    }
  } catch {
    console.log(`${DEJAVOO_TOP_LOG_PREFIX} ${phase}`, String(detail))
  }
}

function isAndroidNative () {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

function isIosNative () {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
}

function isTopNativePlatform () {
  return isAndroidNative() || isIosNative()
}

/**
 * Whether the UI should offer Tap to Pay on this device (native Android/iOS + backend config).
 * Does not require NFC so cashiers still see the option when config is ready; pay-time checks enforce NFC/location on Android.
 *
 * @returns {Promise<{ offered: boolean, reason: string, cfg: object|null, nfcFromPlugin: boolean|null, platformOk: boolean }>}
 */
export async function getTapToPayOfferState () {
  const state = {
    offered: false,
    reason: 'init',
    cfg: null,
    nfcFromPlugin: null,
    platformOk: false
  }

  if (!isTopNativePlatform()) {
    state.reason = 'not_native_mobile'
    topLog('getTapToPayOfferState', { offered: false, reason: state.reason, platformOk: false })
    return state
  }
  state.platformOk = true

  if (isIosNative()) {
    const { getTapToPayOfferStateIos } = await import('./DejavooTop.ios.js')
    return getTapToPayOfferStateIos()
  }

  const { getTapToPayOfferStateNative } = await import('./DejavooTop.native.js')
  return getTapToPayOfferStateNative()
}

/**
 * Sales Register inline card path: same platform gate as ToP plus backend/plugin offer state.
 * Use this instead of Quasar `$q.capacitor`, which can be unset while Capacitor is active.
 */
export async function shouldTryInlineTapToPayOnRegister () {
  if (!isTopNativePlatform()) {
    return false
  }
  const state = await getTapToPayOfferState()
  return state.offered
}

/** iOS: show setup wizard promo when Admin iPhone TPN is configured but device is not registered. */
export async function getIphoneTopWizardEligibility () {
  if (!isIosNative()) {
    return { showWizard: false, cfg: null, deviceReady: false, iphoneConfigReady: false }
  }
  const { getIphoneTopWizardEligibility: getIos } = await import('./DejavooTop.ios.js')
  return getIos()
}

const POS_WARM_DEBOUNCE_MS = 2000
let posWarmupTimer = null

/**
 * Debounced, fire-and-forget warm-up when POS is active (mount / cart changes).
 * No-op on non-native mobile. Silently ignores failures.
 */
export function scheduleDejavooTopWarmupFromPos () {
  if (typeof globalThis === 'undefined') {
    return
  }
  if (!isTopNativePlatform()) {
    return
  }
  if (posWarmupTimer != null) {
    globalThis.clearTimeout(posWarmupTimer)
  }
  posWarmupTimer = globalThis.setTimeout(() => {
    posWarmupTimer = null
    void runDejavooTopPosWarmup()
  }, POS_WARM_DEBOUNCE_MS)
}

async function runDejavooTopPosWarmup () {
  if (!isTopNativePlatform()) {
    return
  }
  if (isIosNative()) {
    const { iosTopWarmRecentlyCompleted, runDejavooTopPosWarmupIos } = await import('./DejavooTop.ios.js')
    if (iosTopWarmRecentlyCompleted()) {
      topLog('posWarmup.skipped', { reason: 'recent_lifecycle_warm' })
      return
    }
    await runDejavooTopPosWarmupIos()
    return
  }
  const { runDejavooTopPosWarmupNative } = await import('./DejavooTop.native.js')
  await runDejavooTopPosWarmupNative()
}

/**
 * Native ensureSession only (no sale, no registerDejavooMobileTerminal). Best-effort.
 */
export async function warmTapToPaySession () {
  if (!isTopNativePlatform()) {
    return
  }
  if (isIosNative()) {
    const { warmTapToPaySessionIos } = await import('./DejavooTop.ios.js')
    await warmTapToPaySessionIos()
    return
  }
  const { warmTapToPaySessionNative } = await import('./DejavooTop.native.js')
  await warmTapToPaySessionNative()
}

/**
 * In-app Tap to Pay via Dejavoo ToP SDK (Capacitor plugin).
 * Returns { handled: false } to fall back to other flows (caller decides).
 */
export async function tryDejavooTapToPayInApp ({
  splitLegIndex = null,
  saleId,
  amount,
  taxAmount,
  saleRefForSdk
}) {
  if (!isTopNativePlatform()) {
    topLog('tryFlow.exit', { handled: false, why: 'not_native_mobile' })
    return { handled: false }
  }

  if (isIosNative()) {
    const { tryDejavooTapToPayInAppIos } = await import('./DejavooTop.ios.js')
    return tryDejavooTapToPayInAppIos({
      saleId,
      amount,
      taxAmount,
      saleRefForSdk,
      splitLegIndex
    })
  }

  const { tryDejavooTapToPayInAppNative } = await import('./DejavooTop.native.js')
  return tryDejavooTapToPayInAppNative({
    saleId,
    amount,
    taxAmount,
    saleRefForSdk,
    splitLegIndex
  })
}
